import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Row,
  Col,
} from "react-bootstrap";
import axios from "axios";

const Settings = () => {
  const [settings, setSettings] = useState({
    default_expiry_days: 365,
    grace_period_days: 7,
    token_ttl_hours: 24,
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Fetch settings
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await axios.get("/api/settings");
        if (response.data) {
          setSettings(response.data);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching settings:", error);
        if (error.response && error.response.status === 401) {
          setError("Your session has expired. Please log in again.");
          // The axios interceptor will handle the logout
        } else {
          setError("Failed to load settings. Please refresh the page.");
        }
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: parseInt(value, 10),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await axios.put("/api/settings", settings);

      setSuccess("Settings updated successfully!");
    } catch (error) {
      console.error("Error updating settings:", error);
      if (error.response && error.response.status === 401) {
        setError("Your session has expired. Please log in again.");
        // The axios interceptor will handle the logout
      } else {
        setError("Failed to update settings. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <h2 className="my-4">License Settings</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      {success && <Alert variant="success">{success}</Alert>}

      <Card>
        <Card.Body>
          {loading ? (
            <p className="text-center">Loading settings...</p>
          ) : (
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Default License Expiry (days)</Form.Label>
                    <Form.Control
                      type="number"
                      name="default_expiry_days"
                      value={settings.default_expiry_days}
                      onChange={handleChange}
                      min="1"
                      required
                    />
                    <Form.Text className="text-muted">
                      Default number of days until a license expires
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Grace Period (days)</Form.Label>
                    <Form.Control
                      type="number"
                      name="grace_period_days"
                      value={settings.grace_period_days}
                      onChange={handleChange}
                      min="0"
                      required
                    />
                    <Form.Text className="text-muted">
                      Number of days a license can be used offline
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Token TTL (hours)</Form.Label>
                    <Form.Control
                      type="number"
                      name="token_ttl_hours"
                      value={settings.token_ttl_hours}
                      onChange={handleChange}
                      min="1"
                      required
                    />
                    <Form.Text className="text-muted">
                      Time-to-live for license tokens in hours
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Button
                variant="primary"
                type="submit"
                disabled={saving}
                className="mt-3"
              >
                {saving ? "Saving..." : "Save Settings"}
              </Button>
            </Form>
          )}
        </Card.Body>
      </Card>

      <Card className="mt-4">
        <Card.Header>Security Settings</Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Card.Title>RSA Keys</Card.Title>
              <Card.Text>
                The license server uses RSA keys to sign and verify license
                tokens.
              </Card.Text>
              <Button variant="secondary">Download Public Key</Button>
            </Col>

            <Col md={6}>
              <Card.Title>Server Health</Card.Title>
              <Card.Text>
                Server Status: <span className="text-success">Operational</span>
              </Card.Text>
              <Button variant="secondary">View Logs</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Settings;
